// Shopping cart kept on the device, mirroring the mobile app's cart (lib/ecommerce/cart_store.dart).
// Only a snapshot of each product is kept; the server re-checks price and stock at quote/checkout time.
// Saved in sessionStorage (same store the app already uses for the auth token/user, see
// AuthenticateService.js) so it is cleared automatically on logout, and kept separately per signed-in
// user so a shared browser never mixes carts between accounts.

export const CART_MAX_LINES = 50;
const CART_EVENT = 'cart:change';

function cartKey() {
  try {
    const userString = sessionStorage.getItem('user');
    const user = userString ? JSON.parse(userString) : null;
    const id = user && (user.id || user.username || user.phone);
    if (id) return `cart_items_${id}`;
  } catch (e) {
    // fall through to the guest cart
  }
  return 'cart_items_guest';
}

function readItems() {
  try {
    const saved = sessionStorage.getItem(cartKey());
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return []; // a corrupt cart is not worth breaking the page for
  }
}

function writeItems(items) {
  try {
    sessionStorage.setItem(cartKey(), JSON.stringify(items));
  } catch (e) {
    // the cart still works for the rest of this page load even if storage fails
  }
  window.dispatchEvent(new CustomEvent(CART_EVENT));
}

function toNumber(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** Builds a cart line from a product returned by POST /ecommerce/items. */
export function itemFromProduct(product, qty = 1) {
  return {
    id: parseInt(product.id, 10) || 0,
    name: product.name || '',
    image: product.cover_img || '',
    price: toNumber(product.amount),
    listPrice: toNumber(product.actual_amount),
    deliveryFee: toNumber(product.delivery_fee),
    stock: parseInt(product.quantity, 10) || 0,
    qty,
  };
}

export const cartStorage = {
  /** Subscribe to cart changes (add/remove/qty/sync in this tab, from any component). Returns an unsubscribe fn. */
  subscribe(listener) {
    window.addEventListener(CART_EVENT, listener);
    return () => window.removeEventListener(CART_EVENT, listener);
  },

  getItems() {
    return readItems();
  },

  count(items) {
    return (items || readItems()).reduce((sum, i) => sum + i.qty, 0);
  },

  subtotal(items) {
    return (items || readItems()).reduce((sum, i) => sum + i.price * i.qty, 0);
  },

  /** Adds a product, or increases its quantity. Returns an error message, or null on success. */
  add(product, qty = 1) {
    const items = readItems();
    const item = itemFromProduct(product, qty);
    if (!item.id) return 'This product cannot be added to the cart.';
    if (item.stock < 1) return `${item.name} is out of stock.`;

    const index = items.findIndex((i) => i.id === item.id);
    if (index >= 0) {
      const next = items[index].qty + qty;
      if (next > item.stock) return `Only ${item.stock} of ${item.name} available.`;
      items[index] = { ...items[index], qty: next, price: item.price, stock: item.stock };
    } else {
      if (items.length >= CART_MAX_LINES) return `Your cart is full (max ${CART_MAX_LINES} different products).`;
      if (qty > item.stock) return `Only ${item.stock} of ${item.name} available.`;
      items.push(item);
    }
    writeItems(items);
    return null;
  },

  setQty(id, qty) {
    let items = readItems();
    const index = items.findIndex((i) => i.id === id);
    if (index < 0) return;
    if (qty < 1) {
      items = items.filter((i) => i.id !== id);
    } else {
      const max = items[index].stock;
      items[index] = { ...items[index], qty: max > 0 && qty > max ? max : qty };
    }
    writeItems(items);
  },

  remove(id) {
    writeItems(readItems().filter((i) => i.id !== id));
  },

  clear() {
    writeItems([]);
  },

  /** Refreshes price/stock from a fresh product list; drops products that no longer exist or are out of stock. */
  sync(products) {
    const byId = new Map((products || []).map((p) => [parseInt(p.id, 10), p]));
    const removed = [];
    const kept = [];
    for (const item of readItems()) {
      const fresh = byId.get(item.id);
      if (!fresh || (parseInt(fresh.quantity, 10) || 0) < 1) {
        removed.push(item.name);
        continue;
      }
      const updated = itemFromProduct(fresh, item.qty);
      if (updated.qty > updated.stock) updated.qty = updated.stock;
      kept.push(updated);
    }
    writeItems(kept);
    return removed;
  },
};
